import { useState } from 'react';
import { getRecentPortraits } from '../utils/recentPortraits';
import { getWatermarkUrl } from '../utils/watermark';
import { shopifyImg } from '../utils/img';
import { RetryImage } from './RetryImage';

interface Props {
  /** Base path of the preview page, e.g. "/pages/preview". */
  previewPage: string;
}

/**
 * RecentPortraits (landing) — a strip of the visitor's previously generated
 * portraits (from localStorage), each linking back to its shareable preview
 * page. Mirrors the WooCommerce "Recent Portraits" on the upload page. Renders
 * nothing for first-time visitors.
 */
export function RecentPortraits({ previewPage }: Props) {
  // Read once on mount — the list only changes after a generate (which
  // redirects away), so there's nothing to react to on this page.
  const [items] = useState(() => getRecentPortraits());
  const [watermarkUrl] = useState(() => getWatermarkUrl());
  if (items.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="flex items-center gap-2 m-0 text-warm-brown font-bold text-base">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="9" cy="9" r="2" />
          <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
        </svg>
        Recent Portraits
      </h2>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {items.map((v) => (
          <a
            key={v.jobId}
            href={`${previewPage}?id=${encodeURIComponent(v.jobId)}`}
            aria-label={v.styleName ? `View ${v.styleName} portrait` : 'View portrait'}
            className="relative flex-shrink-0 w-[88px] aspect-[3/4] rounded-[12px] overflow-hidden border-2 border-soft-border opacity-90 hover:opacity-100 transition-opacity"
          >
            <RetryImage src={shopifyImg(v.previewUrl, 300)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            <img
              src={watermarkUrl}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 w-full h-full object-cover opacity-[0.85]"
            />
          </a>
        ))}
      </div>
    </section>
  );
}
